/**
 * SF-T-177: approval requests. A run's coding agent in T3 Code can stop on a permission request (a command
 * or tool call its access mode doesn't allow by itself). T3's MCP can't list or answer those, so the machine
 * reads what's waiting from the thread's activity (agent/src/commands.ts `pendingApproval`), and the run
 * records it here when its wait step sees it (worker/src/services/runs/approval-requests.ts).
 *
 * The task then waits on whoever started the run, with a factory note, and its page shows an Approval
 * request card: the command, the agent's last message before it (what and why), and an AI explanation
 * (factory/ai-prompts/approval-explain.md). The person approves it from the factory (SF-T-225: Approve or
 * Approve for session, sent over T3's own RPC socket by t3.approve) or in T3 Code (the card links there), or
 * denies it from the factory with a message: the waiting turn restarts with it (t3.restart). Either way
 * the request is resolved once the agent works again, and the task goes back to the AI builder.
 */

import type { T3ApprovalDecision, T3Question } from './agent-protocol';

/**
 * SF-T-177's follow-up: the agent's own T3 questions (its ask tool, which T3's MCP can list and answer) are
 * kept here too, as kind `question`: the same card, hand-off and Inbox item, answered from the task
 * (t3.respond) rather than denied. The card also links straight to the thread in T3 Code's web app, and to
 * the T3 Code app (`t3-thread://v1/<thread>`).
 */
export const APPROVAL_REQUEST_KINDS = ['approval', 'question'] as const;
export type ApprovalRequestKind = (typeof APPROVAL_REQUEST_KINDS)[number];

export const APPROVAL_REQUEST_STATUSES = ['pending', 'denying', 'denied', 'answering', 'answered', 'approving', 'approved', 'resolved'] as const;
export type ApprovalRequestStatus = (typeof APPROVAL_REQUEST_STATUSES)[number];

/** SF-T-225: the approvals a person can send from the factory (a Decline is the task's Deny, or the chat's own). */
export type ApproveDecision = Extract<T3ApprovalDecision, 'accept' | 'acceptForSession'>;
export const APPROVE_DECISIONS: readonly ApproveDecision[] = ['accept', 'acceptForSession'];

/** How each decision reads on a button and after it's sent. */
export const DECISION_LABELS: Record<T3ApprovalDecision, { button: string; done: string }> = {
  accept: { button: 'Approve', done: 'Approved' },
  acceptForSession: { button: 'Approve for session', done: 'Approved for the session' },
  decline: { button: 'Decline', done: 'Declined' },
};

/** The longest message a person may send the agent with a Deny. */
export const APPROVAL_NOTE_MAX_CHARS = 2_000;

/** The AI's explanation of the request (approval-explain), made after it's recorded. */
export interface ApprovalExplanation {
  /** What the agent is trying to do and why, in a few plain sentences. */
  summary: string;
  /** What running it would change or touch, one line each. */
  effects: string[];
  /** Anything to look at twice before approving; empty when none. */
  risks: string[];
  model: string;
  made_at: string;
}

export interface ApprovalRequest {
  id: string;
  task_id: string;
  run_id: string;
  /** R-<n> of the run that's waiting. */
  run_number: number | null;
  /** The run's machine, where T3 Code shows the request. */
  machine_name: string | null;
  t3_thread_id: string | null;
  /** The command or tool call waiting on approval, as the agent read it; null when it couldn't tell. */
  command: string | null;
  item_type: string | null;
  /** The coding agent's last message before it; for a question the machine couldn't read in full, its summary. */
  context: string | null;
  kind: ApprovalRequestKind;
  /** A question: what it asks, as T3 has it; null when the machine sent only a summary (answer it in T3 Code). */
  questions: T3Question[] | null;
  /** A question: the answers sent, per question id, with picks as their options' labels (option values never leave the hub). */
  answers: Record<string, string | string[]> | null;
  /**
   * T3 Code's address for the thread (`<origin>/<environment>/<thread>`), or only its origin when the
   * machine couldn't read its environment; null when it's only reachable on its machine.
   */
  t3_url: string | null;
  /** The thread in the T3 Code app (`t3-thread://v1/<thread>`); null without a thread. */
  t3_app_url: string | null;
  /**
   * pending: waiting on a person; denying: a Deny is being sent; denied: the turn restarted with the
   * person's message, or (decision `decline`) T3's Decline was sent; answering / answered: a question's
   * answers are being sent / were sent; approving / approved (SF-T-225): an Approve (or a Decline, by its
   * decision) is being sent / was sent; resolved: the agent works again (approved or answered in T3 Code),
   * or the run ended.
   */
  status: ApprovalRequestStatus;
  /** An open request whose run isn't waiting any more (it ended or failed): shown as no longer waiting. */
  stale: boolean;
  /** A Deny (or answers) that never heard back from the run within 5 minutes: it can be sent again. */
  deny_retryable: boolean;
  explanation: ApprovalExplanation | null;
  explanation_error: string | null;
  /** Why the last Deny failed (the request is pending again). */
  error: string | null;
  /** The message a Deny sent the agent. */
  note: string | null;
  /**
   * SF-T-225: the approvals the factory can send for it (Approve, Approve for session), from what T3 offers.
   * Empty when the machine couldn't read T3's own request (an older agent): approve it in T3 Code then.
   */
  approve_decisions: ApproveDecision[];
  /**
   * SF-T-225: whether T3's own Decline can be sent for it: the agent is told no (with the message) and carries
   * on in the same turn, where a Deny restarts its turn. False when the machine couldn't read T3's request.
   */
  can_decline: boolean;
  /** SF-T-225: what was sent through T3: an Approve, Approve for session, or Decline (its message is `note`). */
  decision: T3ApprovalDecision | null;
  decided_by: { user_id: string; name: string } | null;
  decided_at: string | null;
  resolved_at: string | null;
  created_at: string;
  updated_at: string;
}

/** Whether a request still needs a person: the card's open ones. */
export const isOpenApproval = (r: Pick<ApprovalRequest, 'status' | 'stale'>) => OPEN_APPROVAL_STATUSES.includes(r.status) && !r.stale;

/** The statuses a request is open in (waiting on a person, or on the machine to take their answer). */
export const OPEN_APPROVAL_STATUSES: readonly ApprovalRequestStatus[] = ['pending', 'denying', 'answering', 'approving'];

/** SF-T-225: which of T3's offered decisions the factory's Approve buttons can send (none: approve in T3 Code). */
export function approveDecisionsOf(offered: readonly string[] | null | undefined): ApproveDecision[] {
  if (!offered) return [];
  return APPROVE_DECISIONS.filter((d) => offered.includes(d));
}

/** The thread in the T3 Code app; null for an id that isn't plainly one. */
export function t3AppThreadUrl(threadId: string | null): string | null {
  return threadId && /^[A-Za-z0-9:_.-]{1,200}$/.test(threadId) ? `t3-thread://v1/${threadId}` : null;
}

/** The thread in T3 Code's web app, from its origin and environment id; the origin alone when either id isn't plainly one. */
export function t3WebThreadUrl(origin: string, environmentId: string | null | undefined, threadId: string | null): string {
  const plain = (v: string | null | undefined): v is string => !!v && /^[A-Za-z0-9:_.-]{1,200}$/.test(v);
  return plain(environmentId) && plain(threadId) ? `${origin}/${encodeURIComponent(environmentId)}/${encodeURIComponent(threadId)}` : origin;
}
